import { Component, inject, signal } from '@angular/core';
import { InputText } from 'primeng/inputtext';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { Button } from 'primeng/button';
import { TherapistClientApiService } from '@therapist/shared/services/api/therapist-client-api.service';
import { TherapistClientDto } from '@therapist/shared/models/client/therapist-client.dto';
import { Message } from 'primeng/message';
import { TherapistClientStateService } from '@therapist/shared/services/state/therapist-client-state.service';
import { Router, RouterLink } from '@angular/router';
import { TherapistStateService } from '@therapist/shared/services/state/therapist-state.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AutoFocus } from 'primeng/autofocus';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { OnlyNumberDirective } from '../../directives/only-number/only-number.directive';
import { EmailAlreadyInUseValidator } from '../../validators/email-already-in-use.validator';
import { SsnAlreadyInUseValidator } from '../../validators/ssn-already-in-use.validator';

@Component({
  selector: 'sana-tp-client-add',
  imports: [
    InputText,
    ReactiveFormsModule,
    DatePicker,
    Button,
    Message,
    AutoFocus,
    InputGroup,
    InputGroupAddon,
    OnlyNumberDirective,
    RouterLink
  ],
  templateUrl: './client-add-page.component.html',
  styleUrl: './client-add-page.component.scss'
})
export class ClientAddPageComponent {

  readonly #fb = inject(FormBuilder);
  readonly #clientStateService = inject(TherapistClientStateService);
  readonly #clientApiService = inject(TherapistClientApiService);
  readonly #router = inject(Router);
  readonly #therapistStateService = inject(TherapistStateService);
  readonly #emailAlreadyInUseValidator = inject(EmailAlreadyInUseValidator);
  readonly #ssnAlreadyInUseValidator = inject(SsnAlreadyInUseValidator);

  clientForm = this.#fb.group({
    firstName: new FormControl<string | null>(null, [
      Validators.required,
      Validators.maxLength(255)
    ]),
    lastName: new FormControl<string | null>(null, [
      Validators.required,
      Validators.maxLength(255)
    ]),
    email: new FormControl<string | null>(null, {
        validators: [
          Validators.required,
          Validators.email,
          Validators.maxLength(255)
        ],
        asyncValidators: [
          this.#emailAlreadyInUseValidator.validate.bind(this.#emailAlreadyInUseValidator)
        ]
      }
    ),
    titlePrefix: new FormControl(<string | null>null),
    titleSuffix: new FormControl<string | null>(null),
    phoneNumber: new FormControl<string | null>(null, [Validators.pattern(/^\+[1-9]\d{7,14}$/)]),
    socialSecurityNumber: new FormControl<string | null>({ value: null, disabled: false }, {
      validators: [
        Validators.minLength(4),
        Validators.maxLength(4),
        Validators.required
      ],
      asyncValidators: [
        // this.#ssnAlreadyInUseValidator.validate.bind(this.#ssnAlreadyInUseValidator)
      ]
    }),
    dateOfBirth: new FormControl<string | null>(null, [
      Validators.required
    ])
  });

  messages = signal<any[]>([]);

  today = new Date();
  minDate = new Date(1900, 0, 1);
  dateOfBirthSvNr = '';
  birthdateRegex = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/;

  isSaving = false;

  constructor() {
    const firstnameCtrl = this.clientForm.controls.firstName;
    firstnameCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter(value => this.isValidString(value) && this.startsWithLowerCaseRE(value!))
      ).subscribe(value => firstnameCtrl.setValue(firstnameCtrl.value!.charAt(0).toUpperCase()));

    const lastnameCtrl = this.clientForm.controls.lastName;
    lastnameCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter(value => this.isValidString(value) && this.startsWithLowerCaseRE(value!))
      ).subscribe(value => lastnameCtrl.setValue(lastnameCtrl.value!.charAt(0).toUpperCase()));

    const titlePrefixCtrl = this.clientForm.controls.titlePrefix;
    titlePrefixCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter(value => this.isValidString(value) && this.startsWithLowerCaseRE(value!))
      ).subscribe(value => titlePrefixCtrl.setValue(titlePrefixCtrl.value!.charAt(0).toUpperCase()));

    const titleSuffixCtrl = this.clientForm.controls.titleSuffix;
    titleSuffixCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter(value => this.isValidString(value) && this.startsWithLowerCaseRE(value!))
      ).subscribe(value => titleSuffixCtrl.setValue(titleSuffixCtrl.value!.charAt(0).toUpperCase()));

    const dobCtrl = this.clientForm.controls.dateOfBirth;
    dobCtrl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        filter(value => !!value),
        filter(value => this.isValidBirthDate(value!))
      ).subscribe(value => this.setDateOfBirthForSvNr(value!));
  }

  // string in the format dd.mm.yyyy
  setDateOfBirthForSvNr(value: string): void {
    if (!value || value.trim().length === 0) {
      this.dateOfBirthSvNr = '';
      return;
    }

    if (this.isValidBirthDate(value)) {
      const split = value!.toString().split('.');
      const day = split[0];
      const month = split[1];
      const year = split[2].substring(2, 4);

      this.dateOfBirthSvNr = day + month + year;
      return;
    }

    this.dateOfBirthSvNr = '';
  }

  isValidString(value: string | null): boolean {
    return value !== null && value !== undefined && value.trim().length > 0;
  }

  isValidBirthDate(value: string): boolean {
    return this.birthdateRegex.test(value);
  }

  submit(): void {
    this.clientForm.markAllAsTouched();

    if (this.clientForm.invalid) {
      return;
    }

    this.isSaving = true;
    const ctrls = this.clientForm.controls;

    this.#clientApiService.save({
      firstName: ctrls.firstName.value!,
      lastName: ctrls.lastName.value!,
      email: ctrls.email.value!,
      titlePrefix: ctrls.titlePrefix.value!,
      titleSuffix: ctrls.titleSuffix.value!,
      dateOfBirth: ctrls.dateOfBirth.value!,
      socialSecurityNumber: ctrls.socialSecurityNumber.value! + this.dateOfBirthSvNr,
      therapistId: this.#therapistStateService.getCurrentTherapist()()!.id,
      phoneNumber: ctrls.phoneNumber.value
    }).subscribe({
      next: (data: TherapistClientDto) => this.handleClientSaveSuccess(data),
      error: (error) => this.handleClientSaveError(error),
      complete: () => this.isSaving = false
    });
  }

  handleClientSaveSuccess(client: TherapistClientDto) {
    this.messages.set([{ severity: 'success', content: 'Patient gespeichert!' }]);
    this.#clientStateService.setSelectedClient(client);
    this.#router.navigate([`/client/${client.id}`]);
  }

  handleClientSaveError(error: HttpErrorResponse) {
    this.messages.set([{ severity: 'error', content: error.message }]);
  }

  startsWithLowerCaseRE(str: string): boolean {
    return /^[a-z]/.test(str);
  }
}
